import { Skeleton } from '@mantine/core'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OutreachTypeCounts {
  type: string
  automated: number
  assisted: number
  manual: number
  total: number
}

export interface OutreachSummary {
  automated: number
  assisted: number
  manual: number
  total: number
  byType: OutreachTypeCounts[]
}

// ---------------------------------------------------------------------------
// Summary row
// ---------------------------------------------------------------------------

interface SummaryRowProps {
  label: string
  value: number
  maxTotal: number
  color: string
  description: string
}

const SummaryRow = ({
  label,
  value,
  maxTotal,
  color,
  description,
}: SummaryRowProps) => {
  const widthPct = maxTotal > 0 ? (value / maxTotal) * 100 : 0
  const pct = maxTotal > 0 ? Math.round((value / maxTotal) * 100) : 0

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-semibold text-gray-800">{value}</span>
          <span className="text-xs text-gray-400">({pct}%)</span>
        </div>
      </div>
      <div className="h-5 w-full rounded bg-gray-100">
        <div
          className="flex h-full items-center rounded"
          style={{
            width: value > 0 ? `${Math.max(widthPct, 6)}%` : '0%',
            backgroundColor: color,
          }}
        />
      </div>
      <p className="text-[11px] leading-tight text-gray-400">{description}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoEOutreachSummaryChartProps {
  data: OutreachSummary | null | undefined
  loading?: boolean
}

export const VoEOutreachSummaryChart = ({
  data,
  loading = false,
}: VoEOutreachSummaryChartProps) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton height={50} />
        <Skeleton height={50} />
        <Skeleton height={50} />
        <Skeleton height={50} />
      </div>
    )
  }

  if (!data || data.total === 0) {
    return (
      <div className="flex h-[160px] items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <SummaryRow
        label="Automated"
        value={data.automated}
        maxTotal={data.total}
        color="#77BEE7"
        description="System-scheduled emails and voice-agent calls"
      />
      <SummaryRow
        label="Assisted"
        value={data.assisted}
        maxTotal={data.total}
        color="#B9E6FE"
        description="Voice-agent calls launched by a team member"
      />
      <SummaryRow
        label="Manual"
        value={data.manual}
        maxTotal={data.total}
        color="#98A2B3"
        description="Logged directly by team members"
      />
      <SummaryRow
        label="Total"
        value={data.total}
        maxTotal={data.total}
        color="#344054"
        description="All outreach attempts in the period"
      />
    </div>
  )
}
